// useConsoleLocations.js — the locations a signed-in (console) user may use in a Warehouse Portal tool.
//
// Returns null while loading, then the list. The list is:
//   • active locations of the store open in the sidebar (stores themselves left out unless includeStores),
//   • only the ones the user is assigned to, unless they are an admin (same rule the API enforces),
//   • narrowed to the location picked in the sidebar nav ("emulating") when there is one — the tools obey it.
// If the sidebar location is not usable in the tool the list is empty rather than falling back to others.
import { useEffect, useMemo, useState } from 'react';
import { authenticatedFetch, useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import { hasRole } from './constants/roles';

// Warehouse tools v2 (2026-10-21) stay on the test site until Eric approves them for prod — same hostname gate as
// Bulk Print Labels. Off: every tool offers all active locations and a site manager still applies a Stock Take.
// The backend mirrors this with WAREHOUSE_V2_ENABLED=1 in the test .env (inventory_counting.py).
export const WAREHOUSE_V2 = window.location.hostname.startsWith('operations-test');

export default function useConsoleLocations(enabled, { includeStores = false } = {}) {
    const { user, emulating } = useAuth();
    const { activeStore } = useStore();
    const [rows, setRows] = useState(null);

    useEffect(() => {
        if (!enabled || !activeStore) return;
        let live = true;
        setRows(null);
        authenticatedFetch(`/api/locations?store=${encodeURIComponent(activeStore)}`)
            .then(r => (r.ok ? r.json() : []))
            .then(list => { if (live) setRows(list || []); })
            .catch(() => { if (live) setRows([]); });
        return () => { live = false; };
    }, [enabled, activeStore]);

    const isAdmin = hasRole(user, ['admin']);
    const userLocs = user?.locations;
    const emuSid = emulating?.shopify_location_id ? String(emulating.shopify_location_id) : '';
    const emuStore = emulating?.store_key || '';

    return useMemo(() => {
        if (!Array.isArray(rows)) return null;
        let out = rows.filter(l => l.active && l.shopify_location_id && l.shopify_location_id !== '0'
            && (includeStores || !l.is_store));
        if (WAREHOUSE_V2 && !isAdmin) {
            const mine = new Set((userLocs || [])
                .filter(l => l.store_key === activeStore && l.shopify_location_id)
                .map(l => String(l.shopify_location_id)));
            out = out.filter(l => mine.has(String(l.shopify_location_id)));
        }
        if (WAREHOUSE_V2 && emuSid && (!emuStore || emuStore === activeStore)) {
            out = out.filter(l => String(l.shopify_location_id) === emuSid);
        }
        return out;
    }, [rows, isAdmin, userLocs, activeStore, emuSid, emuStore, includeStores]);
}
